/** @jsxImportSource @emotion/react */
import { useCallback, useEffect } from "react";
import { SerializedStyles, css } from "@emotion/react";
import { StyledText } from "../../../assets/typography/typography";
import { colors } from "../../colors";
import CurrencyInput from "react-currency-input-field";
import { UseFormReturn, useController } from "react-hook-form";
import * as yup from "yup";
import { getCurrencyDecimals } from "../../../util";

type Props = {
  formMethods: UseFormReturn<any, any>;
  name: string;
  currencyName?: string;
  placeholder?: string;
  label?: string;
  helpertext?: string;
  currencyString?: string;
  additionalStyles?: SerializedStyles;
  onChange?: (value: string | number | undefined) => void;
};

export const validateAboveZero = (currencyName: string) => {
  return yup
    .string()
    .test("Testing if above zero", "Must be at least 0", (value, ctx) => {
      const parsedValue = transformPriceInput(value, ctx.parent[currencyName]);
      return parsedValue >= 0;
    });
};

export const validateAboveOrEqualToZero = (
  value: string | number,
  currency: string
) => {
  const parsedValue = transformPriceInput(value, currency);
  return parsedValue >= 0;
};

export const transformPriceInput = (
  price: string | number | undefined,
  currency: string
) => {
  if (!price) {
    return 0;
  }
  const decimals = getCurrencyDecimals(currency);

  if (typeof price === "number") {
    return price * Math.pow(10, decimals);
  }
  return Math.round(Number(price.replace(",", ".")) * Math.pow(10, decimals));
};

export const PriceInput = ({
  name,
  formMethods,
  placeholder,
  currencyName,
  label,
  helpertext,
  currencyString,
  additionalStyles,
  onChange,
}: Props) => {
  const wrapperStyle = css`
    width: 100%;
    ${additionalStyles}
  `;

  const { watch, control, getFieldState, formState } = formMethods;

  const {
    field: { ref, onChange: hookOnChange, value, ...rest },
  } = useController({
    name,
    control,
  });

  const customOnChange = useCallback(
    (value: string | number | undefined) => {
      if (onChange) {
        onChange(value);
      }
      hookOnChange(value);
    },
    [onChange, hookOnChange]
  );

  const currency = currencyName
    ? watch(currencyName)
    : currencyString
    ? currencyString
    : "DKK";

  const errorMessage = getFieldState(name, formState).error?.message;

  // Iceland does not support decimal values.
  useEffect(() => {
    const decimals = getCurrencyDecimals(currency);
    if (decimals === undefined || decimals > 0) {
      return;
    }
    // Watching value inside useEffect to not having to include value in dependency array
    // We do not want the effect to fire when value changes, but only when currency changes
    const value = watch(name);

    if (!value) {
      return;
    }

    customOnChange(Math.round(transformPriceInput(value, currency)));
  }, [currency, customOnChange, name, watch]);

  const inputStyle = css`
    padding: 6px 12px;
    width: 100%;
    min-height: 38px;
    outline: none;
    border: 1px solid ${errorMessage ? colors.errorMedium : colors.greyBorder};
    border-radius: 3px;
    &:focus {
      border: 1px solid
        ${errorMessage ? colors.errorMedium : "rgba(70, 127, 207, 1)"};
      outline: 2px solid
        ${errorMessage ? "rgba(220, 20, 45, 0.2)" : "rgba(70, 127, 207, 0.2)"};
    }
  `;

  return (
    <div css={wrapperStyle}>
      <StyledText
        color={colors.textPrimary}
        className="bodySubtextSemibold mb-2"
      >
        {label ?? `Price (${currency})`}
      </StyledText>
      <CurrencyInput
        css={inputStyle}
        onValueChange={(value) => {
          // When undefined is passed as value we have to set value to empty string: https://github.com/cchanxzy/react-currency-input-field/issues/256 (last comment)
          customOnChange(value || "");
        }}
        onFocus={(e) => {
          e.target.select();
        }}
        ref={ref}
        value={value}
        inputMode="text"
        allowDecimals
        // TODO: Look out for this bugfix: https://github.com/cchanxzy/react-currency-input-field/issues/307
        // Issue is that backspace does not work when deleting "on-the-go" while inputting value. You have to use arrows to go one
        // character back and delete from there
        disableAbbreviations={true}
        // Iceland does not support decimal values. Will fail without this.
        decimalScale={
          getCurrencyDecimals(currency) === 0
            ? undefined
            : getCurrencyDecimals(currency)
        }
        // Intl Config should handle ISK formatting, but value is a string, which results in NaaN
        // useEffect solves this issue
        intlConfig={{
          locale: "en-US",
          currency: currency,
        }}
        placeholder={placeholder ?? "0,00"}
        {...rest}
      />
      {helpertext && !errorMessage && (
        <StyledText
          color={colors.textSecondary}
          className="bodySubtextRegular mt-2 mb-0"
        >
          {helpertext}
        </StyledText>
      )}
      {errorMessage && (
        <StyledText
          color={colors.errorDark}
          className="bodySubtextRegular mt-2 mb-0"
        >
          {`${errorMessage}`}
        </StyledText>
      )}
    </div>
  );
};
